/* Copyright (c) 2020-present CloudIO Inc */

import {
  atom,
  useStateXAtom,
  useStateXAtomValue,
  useStateXSetter,
} from '@cloudio-saas/statex';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ComponentSample, getManager, Platform } from 'componentregistry';
import { appPageLoadedAtom } from 'components/Page/Page';
import Controller from 'controller/Controller';
import { pageAtom } from 'designer/atoms';
import useComponentSize from 'hooks/useComponentSize';
import Icon from 'icons/Icon';
import {
  activeSidebarPanelAtom,
  sidebarActiveComponentAtom,
  sidebarActivePageIdAtom,
  sidebarPageViewAtom,
  sidebarPageWizardTitleAtom,
} from 'layout/Sidebar/atoms';
import { kebabCase } from 'lodash-es';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { itemWithDefaultValues } from 'registry/Components';
import PageContainer from 'root/PageContainer';
import useUser, { isViewOnlyDeveloper, useIsDeveloper } from 'root/useUser';
import { useIoPagesEditStore, useIoPagesStore } from 'store/utils/utils';
import { stopPropagation } from 'utils/events';
import { useUpdateOnHMR } from 'utils/hmr';
import { getErrorMessage } from 'utils/isEmpty';
import { ShareUrl } from 'utils/url';
import WaveDots from '../../../../utils/WaveDots';
import { pagesFilter } from '../PagesView/PagesPanel';
import PropertiesForm from '../PropertiesView/PropertiesForm';
import { cleanupOrphanItems } from '../TreeView/state';
import Markdown from './Markdown';

const useAccordianStyles = makeStyles((theme: Theme) => ({
  root: {
    boxShadow: 'none',
    overflow: 'hidden',
    backgroundColor: 'transparent',
    backgroundImage: 'none',
    margin: 0,
    '&.Mui-expanded': {
      margin: 0,
    },
  },
  header: {
    paddingLeft: 0,
    alignItems: 'center',
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 40,
    '& > div': {
      alignItems: 'center',
      margin: 0,
    },
    '& > .Mui-expanded': {
      margin: 0,
      minHeight: 40,
    },
    '&.Mui-expanded': {
      margin: 0,
      minHeight: 40,
    },
  },
  details: {
    padding: 0,
    paddingTop: 16,
  },
  heading: {
    paddingTop: 26,
    fontSize: theme.typography.pxToRem(26),
    fontWeight: theme.typography.fontWeightRegular,
    lineHeight: '40px',
  },
  form: {
    display: 'block',
    width: '100%',
  },
}));

export const maximizedSampleIdAtom = atom({
  path: ['transient', 'maximizedPageId'],
  defaultValue: '',
  urlParam: true,
});

export default function ComponentSampleRenderer({
  sample,
  appUid,
  roleUid,
  width,
  platform,
}: {
  appUid: string;
  roleUid: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sample: ComponentSample<any>;
  width: number;
  platform: Platform;
}): React.ReactElement | null {
  const aclasses = useAccordianStyles();
  const itemType = useStateXAtomValue(sidebarActiveComponentAtom);
  const mgr = getManager(itemType);
  const set = useStateXSetter();
  const [pending, setPending] = useState(true);
  const listStore = useIoPagesStore(appUid);
  const editStore = useIoPagesEditStore(appUid);
  const [renderProps, setRenderProps] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [busy, setBusy] = useState(false);
  const hmr = useUpdateOnHMR();
  const sampleId = `${mgr.type}-${sample.exampleId}`;
  const pageId = `$sample$${sample.exampleId}`;
  const isDev = useIsDeveloper(appUid);
  const isViewOnlyUser = isViewOnlyDeveloper(platform, appUid);

  useLayoutEffect(() => {
    set(['db', 'IoPages', pageId, 'controller'], sample.controller);
    if (sample.controller) {
      async function run() {
        try {
          await Controller.compile({
            appUid,
            pageId,
            platform,
            source: sample.controller,
            fileName: `${pageId}_PageController.tsx`,
            debug: platform.isDeveloper(appUid),
          });
        } catch (e) {
          platform.showError(
            `Error compiling page controller: ${getErrorMessage(e)}`,
          );
        }
      }
      run();
    }
    set(pageAtom, sample.page, {
      params: { appUid, pageId },
    });
    const item = itemWithDefaultValues(sample.item);
    set([...pageAtom.path, sample.item.itemId], item, {
      params: { appUid, pageId },
    });
    if (sample.item.type !== 'Page') {
      set(
        [...pageAtom.path, sample.item.parent, 'children'],
        (children: string[]) => {
          return sample.clearParentChildren
            ? [sample.item.itemId]
            : children.includes(sample.item.itemId)
            ? children
            : [...children, sample.item.itemId];
        },
        {
          params: { appUid, pageId },
        },
      );
    }
    setPending(false);
    return () => {
      set(pageAtom, undefined, {
        params: { appUid, pageId },
      });
      set(['db', 'IoPages', pageId, 'controller'], undefined);
    };
  }, [appUid, pageId, sample, set, sample.page, platform]);

  useEffect(() => {
    if (!pending) {
      // delay rendering props to allow the page to get rendered first
      // and all the LocalDataStore get's initialized before rendering the props
      setRenderProps(true);
    }
  }, [pending]);
  const s = useComponentSize();
  const ds = useComponentSize();
  const user = useUser();
  const [maximizedPageId, setMaximizedPageId] = useStateXAtom(
    maximizedSampleIdAtom,
  );
  const pageLoaded = useStateXAtomValue(appPageLoadedAtom, {
    params: { appUid, pageId },
  });
  if (pending) {
    return null;
  }
  const maximized = maximizedPageId === sampleId;
  const leftWidth =
    s.width > 1024 && !sample.hideProps ? s.width - 400 - 8 : s.width;
  const rightWidth = s.width > 1024 ? 400 : s.width;
  let height = sample.height ?? 200;

  if (maximized) {
    height = window.innerHeight - 100 - ds.height;
  }

  const content = (
    <Accordion
      className={aclasses.root}
      expanded={maximized || expanded}
      onChange={maximized ? undefined : () => setExpanded((e) => !e)}
      sx={
        maximized
          ? {
              '& .MuiAccordionSummary-content': {
                cursor: 'default',
              },
            }
          : undefined
      }
    >
      <AccordionSummary
        className={aclasses.header}
        expandIcon={<Icon iconName="chevron-down" />}
        sx={{ display: 'flex', justifyContent: 'space-between' }}
      >
        <Typography sx={{ flex: 1 }} className={aclasses.heading}>
          {sample.title}
        </Typography>
        {user.isAuthenticated ? (
          <IconButton
            data-tip="Create a new Page with this example"
            disabled={isViewOnlyUser}
            data-tip-at="left"
            onClick={(e) => {
              e.stopPropagation();
              if (!isDev) {
                platform.showError(
                  'You do not have Developer role assigned for this Application!',
                );
                return;
              }
              setBusy(true);
              const md = platform.getter(pageAtom, {
                params: { appUid, pageId },
              });
              if (!md) {
                return;
              }
              const controller = platform.getter([
                'db',
                'IoPages',
                pageId,
                'controller',
              ]) as string | undefined;
              const [page] = cleanupOrphanItems(md);
              platform.asyncBatchUpdater(async () => {
                try {
                  if (!listStore.hasAutoQueryFired()) {
                    listStore.setAutoQueryFired();
                    await listStore.query({ props: pagesFilter(appUid) });
                  }
                  platform.setter(activeSidebarPanelAtom, 'pages');
                  platform.setter(sidebarPageViewAtom, 'edit');
                  platform.setter(sidebarActivePageIdAtom, '');
                  await editStore.clear();
                  await editStore.createNew({
                    partialRecord: {
                      appUid,
                      md: page,
                      controller,
                      pageName: `${sample.title} Sample Page`,
                      pageUid: kebabCase(`${sample.title} Sample Page`),
                      showInNavigationMenu: 'N',
                    },
                    rs: 'I',
                  });
                  await listStore.createNew({
                    partialRecord: {
                      appUid,
                      showInNavigationMenu: 'Y',
                    },
                    rs: 'I',
                  });
                  platform.setter(sidebarPageWizardTitleAtom, 'Add New Page');
                } finally {
                  setBusy(false);
                }
              });
            }}
          >
            {busy ? (
              <WaveDots active />
            ) : (
              <Icon disabled={isViewOnlyUser} iconName="copy" />
            )}
          </IconButton>
        ) : null}
        {maximized ? (
          <ShareUrl platform={platform} appUid={appUid} user={user} />
        ) : null}
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setMaximizedPageId(maximized ? '' : sampleId);
          }}
          data-tip={maximized ? 'Restore' : 'Maximize'}
        >
          <Icon iconName={maximized ? 'compress-alt' : 'expand-alt'} />
        </IconButton>
      </AccordionSummary>
      <AccordionDetails className={aclasses.details}>
        <Box ref={ds.ref}>
          {sample.description ? (
            <Markdown>{sample.description}</Markdown>
          ) : null}
        </Box>
        <Box
          ref={s.ref}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap-reverse',
            gap: '8px',
          }}
          data-testid={`sample-${pageId}`}
        >
          <Box
            sx={{
              // flexBasis: '360px',
              width: leftWidth,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                display: 'block',
                position: 'relative',
                overflow: 'hidden',
                width: '100%',
                height,
              }}
              data-testid={`page-${pageId}`}
            >
              <PageContainer
                width={leftWidth}
                height={height}
                appUid={appUid}
                roleUid={roleUid}
                pageId={pageId}
                paramString=""
                embedded
              />
            </Box>
          </Box>
          {!renderProps || sample.hideProps ? null : (
            <Box
              sx={{
                width: rightWidth,
                position: 'relative',
                maxHeight: `${height}px`,
                overflow: 'auto',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'divider',
                boxSizing: 'border-box',
              }}
              data-testid={`props-${pageId}`}
              onMouseDown={stopPropagation}
              onMouseUp={stopPropagation}
              // property panel number slider stops working if we stop propagation here!!!
              // onMouseMove={stopPropagation}
            >
              {pageLoaded ? (
                <PropertiesForm
                  mgr={mgr}
                  pageId={pageId}
                  activeItemId={sample.item.itemId}
                  hmr={hmr}
                  appUid={appUid}
                  preventCollapse
                  visibleProps={sample.visibleProps as string[]}
                  platform={platform}
                />
              ) : null}
            </Box>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  return maximized ? (
    <Dialog open fullScreen>
      <DialogContent
        sx={{
          padding: '0 16px',
          '& .MuiAccordionSummary-expandIconWrapper': { display: 'none' },
          overflow: 'hidden',
        }}
      >
        {content}
      </DialogContent>
    </Dialog>
  ) : (
    content
  );
}
